import { useCallback, useEffect, useRef, useState } from 'react';
import {
  errorMessage,
  generateOfferComparison,
  getOfferComparison,
} from './offerComparisonApi';

// Polls the single comparison record for a transaction. GET is pure-read on
// the backend — it never kicks a job. First-time generation + regenerate both
// go through `generate()`, which the UI's Generate / Regenerate buttons call.
//
// Hook contract:
//   1. fetch on mount (safe — no side effects)
//   2. poll while status=PENDING
//   3. expose status (NOT_GENERATED | PENDING | READY | FAILED), isStale,
//      canGenerate, generate(), tooFew, loadError.
//
// Access-check semantics match useOfferAiSummaries: 403/404/401 → disabled
// (feature hidden); any other failure → loadError=true for retry.
const PENDING_POLL_MS = 5000;
const READY_REFRESH_MS = 30000;

const useOfferComparison = (transactionId, { viewerIsPrimary = false } = {}) => {
  const [enabled, setEnabled] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  // Last user-facing message from a failed generate() call — surfaced by the
  // strip so the user sees the real backend error instead of a React dev
  // overlay. Cleared on the next successful generate / refresh.
  const [generateError, setGenerateError] = useState(null);
  // Set when the backend's pre-#306 getOrGenerate returns 409 (too few PA
  // offers with READY summaries). Post-#306, canGenerate=false on a 200
  // response delivers the same signal.
  const [precheckTooFew, setPrecheckTooFew] = useState(false);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    if (!transactionId || !viewerIsPrimary) return;
    try {
      const result = await getOfferComparison(transactionId);
      if (!mounted.current) return;
      setComparison(result);
      setEnabled(true);
      setLoadError(false);
      setPrecheckTooFew(false);
    } catch (err) {
      if (!mounted.current) return;
      const status = err?.response?.status;
      // 409 on GET = backend's pre-pure-read behavior reports "too few PA
      // offers with READY summaries". Treat as tooFew so the strip shows the
      // right nudge instead of a hard error. (New backend returns 200 with
      // canGenerate=false for the same case; both end up at tooFew below.)
      if (status === 409) {
        setComparison(null);
        setEnabled(true);
        setLoadError(false);
        setPrecheckTooFew(true);
        return;
      }
      setEnabled(false);
      setComparison(null);
      setPrecheckTooFew(false);
      // Only treat the request as a real error if the backend actually
      // responded with a non-feature-off status. No status at all (CORS block,
      // timeout, undeployed endpoint) hides the strip silently — same posture
      // as 404 / 403 / 401. Avoids showing "Couldn't load" on listings that
      // just haven't had the backend rolled out yet.
      setLoadError(!!status && status !== 404 && status !== 403 && status !== 401);
    }
  }, [transactionId, viewerIsPrimary]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; };
  }, [refresh]);

  // Fast poll while PENDING; slower background refresh while READY so hash
  // changes (new offer uploaded, summary regenerated) surface the Stale
  // badge without a page reload. Skips polling entirely while NOT_GENERATED
  // (nothing to watch) or disabled.
  useEffect(() => {
    if (!enabled) return undefined;
    const status = comparison?.status;
    if (!status || status === 'NOT_GENERATED') return undefined;
    const interval = status === 'PENDING' ? PENDING_POLL_MS : READY_REFRESH_MS;
    const id = setInterval(() => { if (mounted.current) refresh(); }, interval);
    return () => clearInterval(id);
  }, [enabled, comparison, refresh]);

  // Unified entry — fires first-time generation OR regenerate, depending on
  // what the backend finds. Keeps the UI simple: one button handler for both.
  // Catches errors so a 4xx/5xx surfaces as an in-strip message instead of
  // bubbling to the React dev overlay.
  const generate = useCallback(async () => {
    if (!transactionId) return;
    setIsGenerating(true);
    setGenerateError(null);
    try {
      await generateOfferComparison(transactionId);
      await refresh();
    } catch (err) {
      if (!mounted.current) return;
      setGenerateError(errorMessage(err, "Couldn't start the comparison. Please try again."));
    } finally {
      if (mounted.current) setIsGenerating(false);
    }
  }, [transactionId, refresh]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    setIsOpen(false);
    refresh();
  }, [refresh]);

  const tooFew = enabled && (
    precheckTooFew
    || (!!comparison && comparison.canGenerate === false)
  );

  return {
    transactionId,
    enabled,
    tooFew,
    loadError,
    comparison,
    isGenerating,
    generateError,
    isOpen,
    open,
    close,
    refresh,
    generate,
    // regenerate kept as alias of generate so the modal's existing
    // onRegenerate wire-up continues to work unchanged.
    regenerate: generate,
  };
};

export default useOfferComparison;
